import React, { useState } from 'react';
import { Modal, Input, Form } from 'antd';
import { usePasswordPolicy } from '../../utils/hooks/usePasswordPolicy';

interface ResetPasswordModalProps {
    open: boolean;
    onOk: (password: string) => Promise<void>;
    onCancel: () => void;
    userName?: string;
}

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({ open, onOk, onCancel, userName }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const { policy, hints } = usePasswordPolicy();

    const validatePassword = (_: any, value: string) => {
        if (!value) return Promise.reject('Please enter a password');

        if (policy) {
            if (policy.length > 0 && value.length < policy.length)
                return Promise.reject(`Password must be at least ${policy.length} characters`);
            if (policy.capitalLetters > 0 && !/[A-Z]/.test(value))
                return Promise.reject(`Must contain at least ${policy.capitalLetters} uppercase letter(s)`);
            if (policy.numbers > 0 && !/[0-9]/.test(value))
                return Promise.reject(`Must contain at least ${policy.numbers} number(s)`);
            if (policy.specialCharacters > 0 && !/[!@#$%^&*(),.?":{}|<>]/.test(value))
                return Promise.reject(`Must contain at least ${policy.specialCharacters} special character(s)`);
        } else {
            if (value.length < 8) return Promise.reject('Password must be at least 8 characters');
            if (!/[A-Z]/.test(value)) return Promise.reject('Must contain at least one uppercase letter');
            if (!/[0-9]/.test(value)) return Promise.reject('Must contain at least one number');
            if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) return Promise.reject('Must contain at least one special character');
        }

        return Promise.resolve();
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);
            await onOk(values.newPassword);
            form.resetFields();
        } catch (error) {
            console.error('Validation failed:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    return (
        <Modal
            title="Reset Password"
            open={open}
            onOk={handleOk}
            onCancel={handleCancel}
            confirmLoading={loading}
            okText="Reset Password"
            cancelText="Cancel"
            okButtonProps={{ style: { backgroundColor: '#095179', color: '#ffffff' } }}
        >
            <Form form={form} layout="vertical">
                <p style={{ marginBottom: 16 }}>Reset password for: <strong>{userName}</strong></p>
                <Form.Item
                    name="newPassword"
                    label="New Password"
                    rules={[{ validator: validatePassword }]}
                >
                    <Input.Password placeholder="Enter new password" />
                </Form.Item>
                <div style={{ fontSize: 12, color: '#666' }}>
                    <p style={{ marginBottom: 4 }}>Password must contain:</p>
                    <ul style={{ paddingLeft: 20, margin: 0 }}>
                        {hints.map((h, i) => <li key={i}>{h.label}</li>)}
                    </ul>
                </div>
            </Form>
        </Modal>
    );
};

export default ResetPasswordModal;
